// src/pages/TeamVisibility/TeamVisibility.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { visibilityApi, type TeamMember } from "../../api/todo";
import { tasksApi } from "../../api/tasks";
import { useAuth } from "../../contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import PersonAvatar from "../../components/people/PersonAvatar";
import {
  Users, ChevronDown, ChevronRight, Briefcase, X, Search, AlertTriangle, Clock, ArrowLeft, ListTodo, User, Calendar, Info,
} from "lucide-react";

const ELEVATED = new Set(["admin", "owner", "superadmin", "founder", "director"]);
// Supervisor is a view-only tier and must NOT see the Team Visibility panel.
const MANAGER_ROLES = new Set(["manager", "team lead", "hod", "hr"]);

// High-contrast, easy-to-read pill colours scoped to this view (the shared
// gt-pill tokens are intentionally muted/low-contrast and used app-wide, so
// we don't touch them â€” these inline styles only affect the employee planner).
type PillStyle = { bg: string; fg: string; bd: string; dot: string };

const STATUS_STYLE: Record<string, PillStyle> = {
  PENDING:     { bg: 'var(--gt-amber-100)', fg: 'var(--gt-amber-800)', bd: 'var(--gt-amber-500)', dot: 'var(--gt-amber-500)' },
  TODO:        { bg: 'var(--gt-amber-100)', fg: 'var(--gt-amber-800)', bd: 'var(--gt-amber-500)', dot: 'var(--gt-amber-500)' },
  IN_PROGRESS: { bg: 'var(--gt-blue-100)', fg: 'var(--gt-blue-800)', bd: 'var(--gt-blue-500)', dot: 'var(--gt-blue-500)' },
  IN_REVIEW:   { bg: 'var(--gt-purple-100)', fg: 'var(--gt-purple-800)', bd: 'var(--gt-purple-500)', dot: 'var(--gt-purple-500)' },
  COMPLETED:   { bg: 'var(--gt-green-100)', fg: 'var(--gt-green-800)', bd: 'var(--gt-green-500)', dot: 'var(--gt-green-500)' },
  DONE:        { bg: 'var(--gt-green-100)', fg: 'var(--gt-green-800)', bd: 'var(--gt-green-500)', dot: 'var(--gt-green-500)' },
  BLOCKED:     { bg: 'var(--gt-red-100)', fg: 'var(--gt-red-800)', bd: 'var(--gt-red-500)', dot: 'var(--gt-red-500)' },
  MISSED:      { bg: 'var(--gt-red-100)', fg: 'var(--gt-red-800)', bd: 'var(--gt-red-500)', dot: 'var(--gt-red-500)' },
  CANCELLED:   { bg: 'var(--gt-slate-100)', fg: 'var(--gt-slate-600)', bd: 'var(--gt-slate-400)', dot: 'var(--gt-slate-400)' },
};

const PRIORITY_STYLE: Record<string, PillStyle> = {
  URGENT: { bg: 'var(--gt-red-100)', fg: 'var(--gt-red-800)', bd: 'var(--gt-red-600)', dot: 'var(--gt-red-600)' },
  HIGH:   { bg: 'var(--gt-orange-100)', fg: 'var(--gt-orange-800)', bd: 'var(--gt-orange-600)', dot: 'var(--gt-orange-600)' },
  MEDIUM: { bg: 'var(--gt-amber-100)', fg: 'var(--gt-amber-900)', bd: 'var(--gt-amber-600)', dot: 'var(--gt-amber-600)' },
  LOW:    { bg: 'var(--gt-blue-100)', fg: 'var(--gt-blue-900)', bd: 'var(--gt-blue-600)', dot: 'var(--gt-blue-600)' },
};

// Vivid bordered pill â€” far more legible than the cream-on-cream gt-pill.
// outline=true renders a white-bg chip (used for priority, so it stays visually
// distinct from the filled status pills even when they share a hue).
const Pill: React.FC<{ s: PillStyle; children: React.ReactNode; dot?: boolean; outline?: boolean }> = ({ s, children, dot, outline }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 9px', borderRadius: 999,
    background: outline ? 'var(--gt-surface-0)' : s.bg,
    color: s.fg, border: `1px solid ${s.bd}`,
    fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em',
    whiteSpace: 'nowrap', lineHeight: 1.3,
  }}>
    {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />}
    {children}
  </span>
);

// Which filter bucket a raw status belongs to.
const statusBucket = (status: string): 'pending' | 'progress' | 'completed' => {
  if (status === 'COMPLETED' || status === 'DONE') return 'completed';
  if (status === 'IN_PROGRESS' || status === 'IN_REVIEW') return 'progress';
  return 'pending';
};

const INITIAL_VISIBLE = 6;

// â”€â”€ Date-range helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
type RangePreset = 'today' | 'week' | 'month' | 'custom';

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Resolve a preset (+ custom inputs) into concrete inclusive YYYY-MM-DD bounds.
const computeRange = (preset: RangePreset, from: string, to: string): { from: string; to: string } => {
  const today = new Date();
  const t0 = ymd(today);
  if (preset === 'today') return { from: t0, to: t0 };
  if (preset === 'week') return { from: t0, to: ymd(addDays(today, 6)) };
  if (preset === 'month') return { from: t0, to: ymd(addDays(today, 29)) };
  return { from: from || t0, to: to || t0 };
};

// Pretty short label like "Jun 30 â€“ Jul 6" for the range readout.
const prettyRange = (from: string, to: string): string => {
  const fmt = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  return from === to ? fmt(from) : `${fmt(from)} â€“ ${fmt(to)}`;
};

function EmployeeDetail({ member }: { member: TeamMember }) {
  const navigate = useNavigate();
  const [personal, setPersonal] = useState<any[]>([]);
  const [assigned, setAssigned] = useState<any[]>([]);
  const [rangeTasks, setRangeTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters + view-more state
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'progress' | 'completed'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'Personal' | 'Assigned'>('all');
  const [showAll, setShowAll] = useState(false);

  // Date-range filter
  const [rangePreset, setRangePreset] = useState<RangePreset>('today');
  const [customFrom, setCustomFrom] = useState<string>(() => ymd(new Date()));
  const [customTo, setCustomTo] = useState<string>(() => ymd(new Date()));
  const range = useMemo(
    () => computeRange(rangePreset, customFrom, customTo),
    [rangePreset, customFrom, customTo],
  );
  const isToday = rangePreset === 'today';

  // Today â†’ visibility endpoint (personal + assigned). Any other range â†’
  // the date-aware task list (assigned tasks only; other employees' personal
  // to-dos aren't exposed by date, so they only appear under Today).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (rangePreset === 'today') {
          const r = await visibilityApi.userToday(member.user_id);
          if (cancelled) return;
          setPersonal(r.data.personal || []);
          setAssigned(r.data.assigned || []);
          setRangeTasks([]);
        } else {
          const r = await tasksApi.list({
            assigned_to: member.user_id,
            due_after: range.from,
            due_before: range.to,
            include_closed: true,
            page_size: 100,
            sort_by: 'due_date',
            sort_dir: 'asc',
          });
          if (cancelled) return;
          setRangeTasks((r.data as any)?.items || []);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load tasks");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [member.user_id, rangePreset, range.from, range.to]);

  const all = useMemo(() => {
    if (isToday) {
      return [
        ...personal.map((t) => ({ ...t, _src: "Personal" as const })),
        ...assigned.map((t) => ({ ...t, _src: "Assigned" as const })),
      ];
    }
    return rangeTasks.map((t) => ({
      id: t.id,
      title: t.title,
      description: t.description,
      status: t.status,
      priority: t.priority,
      due_date: t.due_date,
      _src: "Assigned" as const,
    }));
  }, [isToday, personal, assigned, rangeTasks]);

  // Faceted counts: status chips reflect the active SOURCE filter (and vice
  // versa), so the chip numbers always match what's actually shown.
  const counts = useMemo(() => {
    const base = sourceFilter === 'all' ? all : all.filter((t) => t._src === sourceFilter);
    const c = { all: base.length, pending: 0, progress: 0, completed: 0 };
    base.forEach((t) => { c[statusBucket(t.status)]++; });
    return c;
  }, [all, sourceFilter]);

  const srcCounts = useMemo(() => {
    const base = statusFilter === 'all' ? all : all.filter((t) => statusBucket(t.status) === statusFilter);
    return {
      all: base.length,
      Personal: base.filter((t) => t._src === 'Personal').length,
      Assigned: base.filter((t) => t._src === 'Assigned').length,
    };
  }, [all, statusFilter]);

  const filtered = useMemo(() => all.filter((t) =>
    (statusFilter === 'all' || statusBucket(t.status) === statusFilter) &&
    (sourceFilter === 'all' || t._src === sourceFilter)
  ), [all, statusFilter, sourceFilter]);

  // Reset the expander whenever filters or range change so we don't strand the
  // user in an expanded state on a now-short list.
  useEffect(() => { setShowAll(false); }, [statusFilter, sourceFilter, rangePreset, range.from, range.to]);

  const visible = showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE);
  const hiddenCount = filtered.length - visible.length;

  // Status / source filter chip.
  const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; count?: number }> =
    ({ active, onClick, children, count }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 12px', borderRadius: 999,
        fontSize: 12, fontWeight: 600, cursor: 'pointer',
        background: active ? 'var(--gt-primary)' : 'var(--gt-surface-0)',
        color: active ? 'var(--gt-white)' : 'var(--gt-text-secondary)',
        border: `1px solid ${active ? 'var(--gt-primary)' : 'var(--gt-border-strong)'}`,
        transition: 'all 160ms ease', whiteSpace: 'nowrap',
      }}
    >
      {children}
      {typeof count === 'number' && (
        <span style={{
          fontSize: 10.5, fontWeight: 800,
          padding: '0 6px', borderRadius: 999, minWidth: 18, textAlign: 'center',
          background: active ? 'var(--gt-overlay-white-22)' : 'var(--gt-surface-2)',
          color: active ? 'var(--gt-white)' : 'var(--gt-text-secondary)',
        }}>{count}</span>
      )}
    </button>
  );

  return (
    <div className="px-6 pb-5 pt-4" style={{ background: 'var(--gt-surface-rail-soft)', borderTop: '1px solid var(--gt-divider)' }}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="gt-leaf" />
          <span className="gt-eyebrow">{isToday ? 'Daily Planner â€” Today' : 'Planner â€” Selected range'}</span>
        </div>
        {!loading && !error && all.length > 0 && (
          <span className="gt-numerals" style={{ fontSize: 11, color: 'var(--gt-text-tertiary)', fontWeight: 600 }}>
            {filtered.length} of {all.length} shown
          </span>
        )}
      </div>

      {/* Date-range filter â€” presets + custom from/to */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--gt-text-tertiary)' }}>
          <Calendar className="w-3.5 h-3.5" /> Range
        </span>
        <Chip active={rangePreset === 'today'} onClick={() => setRangePreset('today')}>Today</Chip>
        <Chip active={rangePreset === 'week'} onClick={() => setRangePreset('week')}>Next 7 days</Chip>
        <Chip active={rangePreset === 'month'} onClick={() => setRangePreset('month')}>Next 30 days</Chip>
        <Chip active={rangePreset === 'custom'} onClick={() => setRangePreset('custom')}>Custom</Chip>
        {rangePreset === 'custom' && (
          <span className="inline-flex items-center gap-1.5">
            <input
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="gt-input"
              style={{ height: 32, width: 150, fontSize: 12, padding: '0 8px' }}
              aria-label="From date"
            />
            <span style={{ color: 'var(--gt-text-tertiary)', fontSize: 12 }}>â†’</span>
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
              className="gt-input"
              style={{ height: 32, width: 150, fontSize: 12, padding: '0 8px' }}
              aria-label="To date"
            />
          </span>
        )}
        {!isToday && (
          <span className="gt-numerals" style={{ fontSize: 11, color: 'var(--gt-text-secondary)', fontWeight: 600, marginLeft: 2 }}>
            {prettyRange(range.from, range.to)}
          </span>
        )}
      </div>

      {/* Range note â€” personal to-dos only surface under Today */}
      {!isToday && !loading && !error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 11.5, color: 'var(--gt-text-tertiary)' }}>
          <Info className="w-3.5 h-3.5" style={{ flexShrink: 0 }} />
          <span>Showing <strong style={{ color: 'var(--gt-text-secondary)' }}>assigned tasks</strong> by due date â€” personal to-dos appear under <em className="gt-serif-italic">Today</em>.</span>
        </div>
      )}

      {/* Filter chips â€” status (by bucket) + source */}
      {!loading && !error && all.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Chip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} count={counts.all}>All</Chip>
          <Chip active={statusFilter === 'pending'} onClick={() => setStatusFilter('pending')} count={counts.pending}>Pending</Chip>
          <Chip active={statusFilter === 'progress'} onClick={() => setStatusFilter('progress')} count={counts.progress}>In Progress</Chip>
          <Chip active={statusFilter === 'completed'} onClick={() => setStatusFilter('completed')} count={counts.completed}>Completed</Chip>
          <span style={{ width: 1, height: 20, background: 'var(--gt-border-strong)', margin: '0 2px' }} />
          <Chip active={sourceFilter === 'all'} onClick={() => setSourceFilter('all')} count={srcCounts.all}>Any source</Chip>
          <Chip active={sourceFilter === 'Personal'} onClick={() => setSourceFilter('Personal')} count={srcCounts.Personal}>
            <User className="w-3 h-3" /> Personal
          </Chip>
          <Chip active={sourceFilter === 'Assigned'} onClick={() => setSourceFilter('Assigned')} count={srcCounts.Assigned}>
            <Briefcase className="w-3 h-3" /> Assigned
          </Chip>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-2 gt-page-sub">
          <div className="gt-loading-ring" style={{ width: 16, height: 16, borderWidth: 2 }} />
          <span>Loadingâ€¦</span>
        </div>
      ) : error ? (
        <Pill s={STATUS_STYLE.MISSED}><AlertTriangle className="w-3 h-3" /> {error}</Pill>
      ) : all.length === 0 ? (
        <p className="gt-page-sub gt-serif-italic">{isToday ? 'No tasks scheduled for today.' : 'No assigned tasks due in this range.'}</p>
      ) : filtered.length === 0 ? (
        <p className="gt-page-sub gt-serif-italic">No tasks match this filter.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {visible.map((t) => {
              const ss = STATUS_STYLE[t.status] || STATUS_STYLE.PENDING;
              const ps = t.priority ? (PRIORITY_STYLE[t.priority] || STATUS_STYLE.CANCELLED) : null;
              const src: PillStyle = t._src === 'Personal'
                ? { bg: 'var(--gt-green-soft)', fg: 'var(--gt-primary)', bd: 'var(--gt-green-muted)', dot: 'var(--gt-primary)' }
                : { bg: 'var(--gt-slate-200)', fg: 'var(--gt-slate-700)', bd: 'var(--gt-slate-400)', dot: 'var(--gt-slate-700)' };
              return (
                <div
                  key={`${t._src}-${t.id}`}
                  onClick={() => t._src === "Assigned" && navigate(`/tasks/${t.id}`)}
                  className={`gt-card p-3 ${t._src === "Assigned" ? "cursor-pointer" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="gt-display flex-1 line-clamp-2" style={{ fontSize: 13, fontWeight: 600, color: 'var(--gt-text-primary)', lineHeight: 1.35 }}>
                      {t.title}
                    </p>
                    <Pill s={ss} dot>{(t.status || "").replace(/_/g, " ")}</Pill>
                  </div>

                  {t.description && (
                    <p
                      className="gt-page-sub line-clamp-2"
                      style={{ fontSize: 12, color: 'var(--gt-text-tertiary)', lineHeight: 1.4, marginBottom: 8 }}
                      title={t.description}
                    >
                      {t.description}
                    </p>
                  )}

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Pill s={src}>
                      {t._src === "Personal" ? <User className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                      {t._src}
                    </Pill>
                    {ps && <Pill s={ps} dot outline>{t.priority}</Pill>}
                    {t.due_date && (
                      <span className="gt-numerals" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 9px', borderRadius: 999,
                        background: 'var(--gt-surface-2)', color: 'var(--gt-text-secondary)',
                        border: '1px solid var(--gt-border)',
                        fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
                      }}>
                        <Clock className="w-3 h-3" />
                        {t.due_date.slice(0, 10)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* View more / Show less â€” reveals every task in the current filter */}
          {filtered.length > INITIAL_VISIBLE && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="gt-btn is-ghost is-sm mt-3"
              style={{ fontWeight: 600 }}
            >
              {showAll ? (
                <>Show less <ChevronDown className="w-3.5 h-3.5" style={{ transform: 'rotate(180deg)' }} /></>
              ) : (
                <>View {hiddenCount} more <ChevronDown className="w-3.5 h-3.5" /></>
              )}
            </button>
          )}
        </>
      )}

      <div className="flex items-center gap-2 mt-4 flex-wrap" style={{ borderTop: '1px dashed var(--gt-border)', paddingTop: 14 }}>
        <button
          onClick={() => navigate(`/tasks/list?assigned_to=${member.user_id}&include_closed=true`)}
          className="gt-btn is-soft is-sm"
        >
          <ListTodo className="w-3.5 h-3.5" />
          View all tasks for {member.name}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function TeamVisibility() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = (user?.role || "").toLowerCase();
  const canAccess = ELEVATED.has(role) || MANAGER_ROLES.has(role);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await visibilityApi.team();
      setMembers(res.data.items || []);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Unique designations/roles for the filter dropdown
  const designations = useMemo(() => {
    const seen = new Set<string>();
    const opts: string[] = [];
    members.forEach((m) => {
      const d = (m.designation || m.role || "").trim();
      if (d && !seen.has(d)) { seen.add(d); opts.push(d); }
    });
    return opts.sort();
  }, [members]);

  if (!canAccess) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-sm text-center" style={{ borderColor: 'var(--gt-overlay-red-18)' }}>
          <div className="inline-flex items-center justify-center mb-3" style={{ width: 48, height: 48, borderRadius: 999, background: 'var(--gt-status-overdue-bg)', color: 'var(--gt-prio-urgent-fg)' }}>
            <X className="w-6 h-6" />
          </div>
          <h2 className="gt-display" style={{ fontSize: 16, color: 'var(--gt-prio-urgent-fg)' }}>Access <em className="gt-serif-italic">Denied</em></h2>
          <p className="gt-page-sub mt-2">Only managers and admins can view employee task visibility.</p>
        </div>
      </div>
    );
  }

  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.designation || "").toLowerCase().includes(q) ||
      (m.role || "").toLowerCase().includes(q);
    const matchRole = roleFilter === "ALL" ||
      (m.designation || m.role || "").trim() === roleFilter;
    return matchSearch && matchRole;
  });

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-5 gt-fade-in">
        <button onClick={() => navigate(-1)} className="gt-btn is-ghost is-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Daily Planner
        </button>

        {/* Header â€” title left, filters right */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 className="gt-page-title">
              Employee <em>Planner</em>
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 4 }}>
              {loading
                ? <>Loading <em className="gt-serif-italic">team</em>â€¦</>
                : <><span className="gt-display gt-numerals" style={{ color: 'var(--gt-text-primary)' }}>{filtered.length}</span> employee{filtered.length !== 1 ? "s" : ""} â€” click any row to see their <em className="gt-serif-italic">daily planner</em>.</>}
            </p>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div className="gt-search-wrap" style={{ width: 220 }}>
              <Search />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, roleâ€¦"
                className="gt-input is-search"
              />
            </div>
            {designations.length > 0 && (
              <div style={{ width: 170, flexShrink: 0 }}>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger style={{ height: 36, fontSize: 12.5 }}>
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Roles</SelectItem>
                    {designations.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="gt-loading-ring" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="gt-card p-12 text-center">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><Users className="w-6 h-6" /></div>
              <div className="gt-empty-title">No employees found</div>
              <div className="gt-page-sub">{search ? "Try a different search term." : "No team members are visible for your role."}</div>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {filtered.map((m) => {
              const isExpanded = expanded === m.user_id;
              return (
                <motion.div
                  key={m.user_id}
                  layout
                  className="gt-card overflow-hidden"
                  style={{ padding: 0 }}
                >
                  <button
                    onClick={() => setExpanded(isExpanded ? null : m.user_id)}
                    className="w-full flex items-center gap-4 px-5 py-4 transition-colors text-left"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--gt-surface-rail-soft)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <PersonAvatar
                      userId={m.user_id}
                      photoUrl={m.profile_photo_url}
                      name={m.name}
                      size={42}
                      className="shrink-0"
                    />

                    <div className="flex-1 min-w-0">
                      <p className="gt-display truncate" style={{ fontSize: 14, fontWeight: 700, color: 'var(--gt-text-primary)' }}>
                        {m.name || m.email || "Unknown"}
                      </p>
                      <p className="gt-page-sub truncate" style={{ marginTop: 2 }}>
                        <span className="gt-eyebrow" style={{ marginBottom: 0, fontSize: 9.5 }}>
                          {m.designation || m.role || "Employee"}
                        </span>
                        {m.email && <span style={{ marginLeft: 8 }}>Â· {m.email}</span>}
                      </p>
                    </div>

                    <div className="shrink-0">
                      <span className="gt-icon-btn is-sm" style={{ pointerEvents: 'none' }}>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </span>
                    </div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        key="detail"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                      >
                        <EmployeeDetail member={m} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
